import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileDown, TrendingUp, Loader2 } from "lucide-react";

const fq = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n || 0);

const SocieteCaTable = ({ s }) => {
  const tot = (y, k) => s.rows.reduce((a, r) => a + (r.annees[y]?.[k] || 0), 0);
  return (
    <div className="mb-5 last:mb-0" data-testid={`ca-annuel-societe-${s.societe_id}`}>
      <p className="text-sm font-semibold mb-2">{s.societe}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-muted-foreground border-b">
            <th className="py-2 font-medium">Produit</th>
            {s.years.map((y) => [
              <th key={`q${y}`} className="py-2 font-medium text-right">Qté {y}</th>,
              <th key={`h${y}`} className="py-2 font-medium text-right">CA HT {y}</th>,
            ])}
          </tr></thead>
          <tbody className="divide-y">
            {s.rows.map((r, i) => (
              <tr key={i}>
                <td className="py-2 pr-2">{r.produit}</td>
                {s.years.map((y) => [
                  <td key={`q${y}`} className="py-2 tabular text-right">{fq(r.annees[y].q)}</td>,
                  <td key={`h${y}`} className="py-2 tabular text-right">{fmtDA(r.annees[y].ht)}</td>,
                ])}
              </tr>
            ))}
            <tr className="font-bold border-t-2 border-primary/30" data-testid={`ca-annuel-total-${s.societe_id}`}>
              <td className="py-2">TOTAL</td>
              {s.years.map((y) => [
                <td key={`q${y}`} className="py-2 tabular text-right">{fq(tot(y, "q"))}</td>,
                <td key={`h${y}`} className="py-2 tabular text-right text-success">{fmtDA(tot(y, "ht"))}</td>,
              ])}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const CaAnnuelCard = ({ societeId }) => {
  const [data, setData] = useState(null);
  const sid = societeId !== "all" ? societeId : undefined;

  useEffect(() => {
    setData(null);
    api.get("/pdf/ca-annuel/data", { params: { societe_id: sid } }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [societeId]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-ca-annuel">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Chiffre d'affaires par année (quantité &amp; valeur HT)</h3>
        </div>
        <Button variant="outline" size="sm" className="gap-2" data-testid="ca-annuel-pdf-btn"
          onClick={() => downloadPdf(`/pdf/ca-annuel${sid ? `?societe_id=${sid}` : ""}`, "chiffre-affaires-annuel")}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
      </div>
      {data === null
        ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : data.map((s) => <SocieteCaTable key={s.societe_id} s={s} />)}
    </div>
  );
};
